import type { CanvasElement, ChecklistItem as FlatChecklistItem } from '../types/schema'
import { createStripboardRecordDefaults } from './stripboardDefaults'
import type { ChecklistItem as TreeChecklistItem, Goal, SubTask, Task, TimelineDate } from '../types/timeline'
import type { FlatWorkspaceBundle } from '../types/sync'

const DEFAULT_GOAL_COLOR = '#6366f1' as const // mosaic-keep: data (default timeline goal hex color)
const DEFAULT_TASK_COLOR = '#3b82f6' as const // mosaic-keep: data (default timeline task hex color)
const DEFAULT_SUBTASK_COLOR = '#60a5fa' as const // mosaic-keep: data (default timeline subtask hex color)
const DEFAULT_DATE: TimelineDate = ''

const ensureText = (value: unknown) => (typeof value === 'string' ? value : '')
const ensureTitle = (value: unknown, fallback: string) => {
  const title = ensureText(value).trim()
  return title || fallback
}

const ensureWeightage = (value: unknown) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 1
)

const ensureProgress = (value: unknown) => (
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(100, Math.max(0, Math.round(value)))
    : 0
)

const getPositionSortValue = (item: FlatChecklistItem) => {
  const position = item.position as unknown
  if (typeof position === 'number' && Number.isFinite(position)) return position
  if (position && typeof position === 'object') {
    const candidate = position as { x?: unknown; y?: unknown }
    const x = typeof candidate.x === 'number' && Number.isFinite(candidate.x) ? candidate.x : 0
    const y = typeof candidate.y === 'number' && Number.isFinite(candidate.y) ? candidate.y : 0
    return y * 1_000_000 + x
  }
  return 0
}

const sortByInputOrder = <T extends { id: string }>(items: T[], sourceOrder: Map<string, number>) =>
  [...items].sort((left, right) => (sourceOrder.get(left.id) ?? 0) - (sourceOrder.get(right.id) ?? 0))

const getContainerChecklist = (
  checklistItems: FlatChecklistItem[],
  containerId: string,
): TreeChecklistItem[] => checklistItems
  .filter((item) => item.parentContainerId === containerId)
  .sort((left, right) => getPositionSortValue(left) - getPositionSortValue(right))
  .map((item): TreeChecklistItem => {
    const node: TreeChecklistItem = {
      id: item.id,
      text: ensureTitle(item.title, 'Untitled checklist item'),
      description: ensureText(item.description),
      completionCriteria: ensureText(item.completionCriteria),
      isCompleted: Boolean(item.isCompleted),
      weightage: ensureWeightage(item.weightage),
    }

    if (typeof item.priority === 'number') node.priority = item.priority
    if (typeof item.complexityStars === 'number') node.complexityStars = item.complexityStars
    if (typeof item.urgencyStars === 'number') node.urgencyStars = item.urgencyStars
    if (typeof item.importanceStars === 'number') node.importanceStars = item.importanceStars
    if (typeof item.estimatedHours === 'number' || item.estimatedHours === null) node.estimatedHours = item.estimatedHours
    if (typeof item.actualCost === 'number' || item.actualCost === null) node.actualCost = item.actualCost
    if (typeof item.currencyCode === 'string') node.currencyCode = item.currencyCode
    if (typeof item.budgetLimit === 'number' || item.budgetLimit === null) node.budgetLimit = item.budgetLimit
    if (typeof item.estimatedCost === 'number' || item.estimatedCost === null) node.estimatedCost = item.estimatedCost
    if (typeof item.soldUnits === 'number' || item.soldUnits === null) node.soldUnits = item.soldUnits
    if (item.scheduleMode !== undefined && item.scheduleMode !== null) node.scheduleMode = item.scheduleMode
    if (typeof item.scheduleStartDate === 'string' || item.scheduleStartDate === null) node.scheduleStartDate = item.scheduleStartDate
    if (typeof item.scheduleEndDate === 'string' || item.scheduleEndDate === null) node.scheduleEndDate = item.scheduleEndDate
    if (typeof item.hasTimeWindow === 'boolean') node.hasTimeWindow = item.hasTimeWindow
    if (typeof item.scheduleTimeFrom === 'string' || item.scheduleTimeFrom === null) node.scheduleTimeFrom = item.scheduleTimeFrom
    if (typeof item.scheduleTimeTo === 'string' || item.scheduleTimeTo === null) node.scheduleTimeTo = item.scheduleTimeTo
    if (item.workflowStatus !== undefined && item.workflowStatus !== null) node.workflowStatus = item.workflowStatus
    if (item.checklistType !== undefined && item.checklistType !== null) node.checklistType = item.checklistType
    if (item.requiresProofFile !== undefined) node.requiresProofFile = Boolean(item.requiresProofFile)
    if (item.requiresCoSign !== undefined) node.requiresCoSign = Boolean(item.requiresCoSign)
    if (item.coSignerRoleTarget != null) node.coSignerRoleTarget = item.coSignerRoleTarget
    if (item.requiresLocationPin !== undefined) node.requiresLocationPin = Boolean(item.requiresLocationPin)
    if (item.targetCoordinates != null) node.targetCoordinates = item.targetCoordinates
    if (typeof item.countdownHours === 'number' || item.countdownHours === null) node.countdownHours = item.countdownHours
    if (item.enforcePaintSpringDelay !== undefined) node.enforcePaintSpringDelay = Boolean(item.enforcePaintSpringDelay)
    if (typeof item.delayDurationHours === 'number' || item.delayDurationHours === null) node.delayDurationHours = item.delayDurationHours
    if (item.proofApproverId != null) node.proofApproverId = item.proofApproverId
    if (item.proofApproverName != null) node.proofApproverName = item.proofApproverName
    if (Array.isArray(item.attachedFormIds)) node.attachedFormIds = item.attachedFormIds
    if (Array.isArray(item.attachedDesignIds)) node.attachedDesignIds = item.attachedDesignIds
    if (item.cardBackgroundFileId != null) node.cardBackgroundFileId = item.cardBackgroundFileId
    if (item.cardBackgroundUrl != null) node.cardBackgroundUrl = item.cardBackgroundUrl
    if (item.cardBackgroundFileName != null) node.cardBackgroundFileName = item.cardBackgroundFileName
    if (item.cardBackgroundStoragePath != null) node.cardBackgroundStoragePath = item.cardBackgroundStoragePath
    if (item.isHelperVisible !== undefined) node.isHelperVisible = item.isHelperVisible !== false
    if (item.helperVisibilityMask != null) node.helperVisibilityMask = item.helperVisibilityMask
    if (item.assigneeId != null) node.assigneeId = item.assigneeId
    if (Array.isArray(item.dependencyNodeIds)) node.dependencyNodeIds = item.dependencyNodeIds
    if (Array.isArray(item.requiredStockIds)) node.requiredStockIds = item.requiredStockIds
    if (Array.isArray(item.stockRequirements)) node.stockRequirements = item.stockRequirements
    if (item.consumptionMode !== undefined && item.consumptionMode !== null) node.consumptionMode = item.consumptionMode
    if (Array.isArray(item.blockedByNodeIds)) node.blockedByNodeIds = item.blockedByNodeIds
    if (Array.isArray(item.isBlockerForNodeIds)) node.isBlockerForNodeIds = item.isBlockerForNodeIds

    return node
  })

/**
 * Transforms flatter Firestore collections into an explicitly ordered nested Goal[] array.
 */
export function transformFlatToNestedTree(
  elements: CanvasElement[],
  checklistItems: FlatChecklistItem[],
): Goal[] {
  const sourceOrder = new Map(elements.map((element, index) => [element.id, index]))
  const flatGoals = sortByInputOrder(elements.filter((element) => element.type === 'GOAL'), sourceOrder)
  const flatTasks = sortByInputOrder(elements.filter((element) => element.type === 'TASK'), sourceOrder)
  const flatSubTasks = sortByInputOrder(elements.filter((element) => element.type === 'SUBTASK'), sourceOrder)

  return flatGoals.map((goal): Goal => {
    const childTasks = flatTasks
      .filter((task) => task.parentId === goal.id)
      .map((task): Task => {
        const childSubTasks = flatSubTasks
          .filter((subTask) => subTask.parentId === task.id)
          .map((subTask): SubTask => ({
            id: subTask.id,
            title: ensureTitle(subTask.title, 'Untitled subtask'),
            description: ensureText(subTask.description),
            startDate: DEFAULT_DATE,
            endDate: DEFAULT_DATE,
            color: DEFAULT_SUBTASK_COLOR,
            checklist: getContainerChecklist(checklistItems, subTask.id),
          }))

        return {
          id: task.id,
          title: ensureTitle(task.title, 'Untitled task'),
          description: ensureText(task.description),
          startDate: DEFAULT_DATE,
          endDate: DEFAULT_DATE,
          color: DEFAULT_TASK_COLOR,
          checklist: getContainerChecklist(checklistItems, task.id),
          subTasks: childSubTasks,
        }
      })

    return {
      id: goal.id,
      title: ensureTitle(goal.title, 'Untitled goal'),
      description: ensureText(goal.description),
      startDate: DEFAULT_DATE,
      endDate: DEFAULT_DATE,
      baseColor: DEFAULT_GOAL_COLOR,
      checklist: getContainerChecklist(checklistItems, goal.id),
      tasks: childTasks,
    }
  })
}

const createChecklistId = (containerId: string, index: number, item: Partial<TreeChecklistItem>) => {
  const rawId = typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `${containerId}-check-${index}`
  return rawId.replaceAll('/', '_')
}

const extractChecklist = (
  legacyList: Partial<TreeChecklistItem>[] | undefined,
  workspaceId: string,
  containerId: string,
  checklistItems: FlatChecklistItem[],
) => {
  if (!Array.isArray(legacyList)) return

  legacyList.forEach((item, index) => {
    const id = createChecklistId(containerId, index, item)
    const flatItem: FlatChecklistItem = {
      ...createStripboardRecordDefaults({ boardStatus: item.workflowStatus || item.scheduleStartDate ? 'TODO' : null, priority: Math.ceil(((item.urgencyStars ?? 1) + (item.importanceStars ?? 1)) / 2), startDate: item.scheduleStartDate ?? null, endDate: item.scheduleEndDate ?? null }),
      id,
      workspaceId,
      parentContainerId: containerId,
      title: ensureTitle(item.text, 'Untitled checklist item'),
      description: ensureText(item.description),
      completionCriteria: ensureText(item.completionCriteria),
      isCompleted: Boolean(item.isCompleted),
      priority: typeof item.priority === 'number' ? item.priority : Math.ceil(((item.urgencyStars ?? 1) + (item.importanceStars ?? 1)) / 2),
      weightage: ensureWeightage(item.weightage),
      position: { x: index, y: 0 },
      prerequisites: [],
      blockedByExternal: null,
      isHelperVisible: item.isHelperVisible !== false,
    }
    if (typeof item.complexityStars === 'number') flatItem.complexityStars = item.complexityStars
    if (typeof item.urgencyStars === 'number') flatItem.urgencyStars = item.urgencyStars
    if (typeof item.importanceStars === 'number') flatItem.importanceStars = item.importanceStars
    if (typeof item.estimatedHours === 'number' || item.estimatedHours === null) flatItem.estimatedHours = item.estimatedHours
    if (typeof item.actualCost === 'number' || item.actualCost === null) flatItem.actualCost = item.actualCost
    if (typeof item.currencyCode === 'string') flatItem.currencyCode = item.currencyCode
    if (typeof item.budgetLimit === 'number' || item.budgetLimit === null) flatItem.budgetLimit = item.budgetLimit
    if (typeof item.estimatedCost === 'number' || item.estimatedCost === null) flatItem.estimatedCost = item.estimatedCost
    if (typeof item.soldUnits === 'number' || item.soldUnits === null) flatItem.soldUnits = item.soldUnits
    if (item.scheduleMode !== undefined && item.scheduleMode !== null) flatItem.scheduleMode = item.scheduleMode
    if (typeof item.scheduleStartDate === 'string' || item.scheduleStartDate === null) flatItem.scheduleStartDate = item.scheduleStartDate
    if (typeof item.scheduleEndDate === 'string' || item.scheduleEndDate === null) flatItem.scheduleEndDate = item.scheduleEndDate
    if (typeof item.hasTimeWindow === 'boolean') flatItem.hasTimeWindow = item.hasTimeWindow
    if (typeof item.scheduleTimeFrom === 'string' || item.scheduleTimeFrom === null) flatItem.scheduleTimeFrom = item.scheduleTimeFrom
    if (typeof item.scheduleTimeTo === 'string' || item.scheduleTimeTo === null) flatItem.scheduleTimeTo = item.scheduleTimeTo
    if (item.workflowStatus !== undefined && item.workflowStatus !== null) flatItem.workflowStatus = item.workflowStatus
    if (item.checklistType !== undefined && item.checklistType !== null) flatItem.checklistType = item.checklistType
    if (item.requiresProofFile !== undefined) flatItem.requiresProofFile = item.requiresProofFile
    if (item.requiresCoSign !== undefined) flatItem.requiresCoSign = item.requiresCoSign
    if (item.coSignerRoleTarget != null) flatItem.coSignerRoleTarget = item.coSignerRoleTarget
    if (item.requiresLocationPin !== undefined) flatItem.requiresLocationPin = item.requiresLocationPin
    if (item.targetCoordinates != null) flatItem.targetCoordinates = item.targetCoordinates
    if (typeof item.countdownHours === 'number' || item.countdownHours === null) flatItem.countdownHours = item.countdownHours
    if (item.enforcePaintSpringDelay !== undefined) flatItem.enforcePaintSpringDelay = item.enforcePaintSpringDelay
    if (typeof item.delayDurationHours === 'number') flatItem.delayDurationHours = item.delayDurationHours
    if (item.proofApproverId != null) flatItem.proofApproverId = item.proofApproverId
    if (item.proofApproverName != null) flatItem.proofApproverName = item.proofApproverName
    if (Array.isArray(item.attachedFormIds)) flatItem.attachedFormIds = item.attachedFormIds
    if (Array.isArray(item.attachedDesignIds)) flatItem.attachedDesignIds = item.attachedDesignIds
    if (item.cardBackgroundFileId != null) flatItem.cardBackgroundFileId = item.cardBackgroundFileId
    if (item.cardBackgroundUrl != null) flatItem.cardBackgroundUrl = item.cardBackgroundUrl
    if (item.cardBackgroundFileName != null) flatItem.cardBackgroundFileName = item.cardBackgroundFileName
    if (item.cardBackgroundStoragePath != null) flatItem.cardBackgroundStoragePath = item.cardBackgroundStoragePath
    if (item.helperVisibilityMask != null) flatItem.helperVisibilityMask = item.helperVisibilityMask
    if (item.assigneeId != null) flatItem.assigneeId = item.assigneeId
    if (Array.isArray(item.dependencyNodeIds)) flatItem.dependencyNodeIds = item.dependencyNodeIds
    if (Array.isArray(item.requiredStockIds)) flatItem.requiredStockIds = item.requiredStockIds
    if (Array.isArray(item.stockRequirements)) flatItem.stockRequirements = item.stockRequirements
    if (item.consumptionMode !== undefined && item.consumptionMode !== null) flatItem.consumptionMode = item.consumptionMode
    if (Array.isArray(item.blockedByNodeIds)) flatItem.blockedByNodeIds = item.blockedByNodeIds
    if (Array.isArray(item.isBlockerForNodeIds)) flatItem.isBlockerForNodeIds = item.isBlockerForNodeIds
    checklistItems.push(flatItem)
  })
}

/**
 * Explodes a nested Goal[] tree back into a flattened document bundle.
 */
export function transformNestedTreeToFlat(
  goals: Goal[],
  workspaceId: string,
): FlatWorkspaceBundle {
  const elements: CanvasElement[] = []
  const checklistItems: FlatChecklistItem[] = []

  goals.forEach((goal) => {
    elements.push({
      ...createStripboardRecordDefaults({ boardStatus: goal.workflowStatus ?? 'TODO', priority: goal.priority ?? 1, startDate: String(goal.startDate), endDate: String(goal.endDate) }),
      id: goal.id,
      workspaceId,
      type: 'GOAL',
      parentId: null,
      title: ensureTitle(goal.title, 'Untitled goal'),
      description: ensureText(goal.description),
      progress: 0,
      weightage: 1,
      assignedEditors: [],
    })
    extractChecklist(goal.checklist, workspaceId, goal.id, checklistItems)

    goal.tasks?.forEach((task) => {
      elements.push({
        ...createStripboardRecordDefaults({ boardStatus: task.workflowStatus ?? 'TODO', priority: task.priority ?? 1, startDate: String(task.startDate), endDate: String(task.endDate) }),
        id: task.id,
        workspaceId,
        type: 'TASK',
        parentId: goal.id,
        title: ensureTitle(task.title, 'Untitled task'),
        description: ensureText(task.description),
        progress: 0,
        weightage: 1,
        assignedEditors: [],
      })
      extractChecklist(task.checklist, workspaceId, task.id, checklistItems)

      task.subTasks?.forEach((subTask) => {
        elements.push({
          ...createStripboardRecordDefaults({ boardStatus: subTask.workflowStatus ?? 'TODO', priority: subTask.priority ?? 1, startDate: String(subTask.startDate), endDate: String(subTask.endDate) }),
          id: subTask.id,
          workspaceId,
          type: 'SUBTASK',
          parentId: task.id,
          title: ensureTitle(subTask.title, 'Untitled subtask'),
          description: ensureText(subTask.description),
          progress: 0,
          weightage: 1,
          assignedEditors: [],
        })
        extractChecklist(subTask.checklist, workspaceId, subTask.id, checklistItems)
      })
    })
  })

  return { elements, checklistItems }
}

export const normalizeFlatBundle = (
  elements: CanvasElement[],
  checklistItems: FlatChecklistItem[],
): FlatWorkspaceBundle => ({
  elements: elements.map((element) => ({
    ...element,
    title: ensureTitle(element.title, 'Untitled item'),
    description: ensureText(element.description),
    progress: ensureProgress(element.progress),
    weightage: ensureWeightage(element.weightage),
    assignedEditors: Array.isArray(element.assignedEditors) ? element.assignedEditors : [],
  })),
  checklistItems: checklistItems.map((item) => ({
    ...item,
    title: ensureTitle(item.title, 'Untitled checklist item'),
    isCompleted: Boolean(item.isCompleted),
    weightage: ensureWeightage(item.weightage),
    prerequisites: Array.isArray(item.prerequisites) ? item.prerequisites : [],
    blockedByExternal: item.blockedByExternal ?? null,
  })),
})
